import { Box, Group, Stack, Text, Title } from '@mantine/core';
import type { ReactElement } from 'react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { accentTokens } from '@/design/VizAccents';
import type { AnalyticsSurface } from './AnalyticsSurface';
import type { TopicDefinition } from './TopicCatalogTypes';

interface TopicComingSoonProps {
    surface: AnalyticsSurface;
    definition: TopicDefinition;
}

/**
 * Placeholder body for a catalog topic flagged `comingSoon`. The topic still
 * appears in the interior nav, but its page shows a dashed "coming soon" panel
 * instead of a live visualization — no query is issued for it.
 */
export default function TopicComingSoon({ surface, definition }: TopicComingSoonProps): ReactElement {
    const tokens = accentTokens(definition.accent);
    const Icon = definition.icon;
    const sideRail = surface.drillStyle === 'side-rail';

    const iconBox = (
        <Box
            style={{
                width: 44,
                height: 44,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: sideRail ? 'var(--bm-color-body)' : tokens.soft,
                border: `1px solid ${tokens.border}`,
                borderRadius: 'var(--bm-radius)',
            }}
        >
            <Icon size={22} color={tokens.solid} />
        </Box>
    );

    return (
        <Box pb="xl">
            {sideRail ?
                <Box
                    mb="lg"
                    p="md"
                    style={{
                        background: tokens.soft,
                        border: `1px solid ${tokens.border}`,
                        borderRadius: 'var(--bm-radius)',
                    }}
                >
                    <Group gap="md" wrap="nowrap" align="flex-start">
                        {iconBox}
                        <Box style={{ minWidth: 0 }}>
                            <Title order={2} fw={700}>
                                {definition.navLabel ?? definition.title}
                            </Title>
                            {definition.question.length > 0 && (
                                <Text size="sm" fw={500} mt={2} c="dimmed">
                                    {definition.question}
                                </Text>
                            )}
                        </Box>
                    </Group>
                </Box>
            :   <PageHeaderComponent title={definition.title} subtitle={definition.question} leading={iconBox} />}

            <Box
                p="xl"
                style={{
                    border: '1px dashed var(--bm-color-border)',
                    borderRadius: 'var(--bm-radius)',
                }}
            >
                <Stack gap={6} align="center" py="xl">
                    <Icon size={28} style={{ color: 'var(--bm-color-text-dimmed)' }} />
                    <Text size="sm" fw={600} mt={6}>
                        Coming soon
                    </Text>
                    <Text size="xs" c="dimmed" ta="center" maw={460}>
                        {definition.hook}
                    </Text>
                </Stack>
            </Box>
        </Box>
    );
}
